import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthCtx = createContext(null);

// Synchronously initialize axios auth header from localStorage BEFORE React renders,
// then attach an interceptor so every request always carries the current token.
const _initialToken = typeof window !== "undefined" ? localStorage.getItem("erp_token") : null;
if (_initialToken) axios.defaults.headers.common["Authorization"] = `Bearer ${_initialToken}`;
axios.interceptors.request.use((config) => {
  const t = typeof window !== "undefined" ? localStorage.getItem("erp_token") : null;
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && typeof window !== "undefined" && window.location.pathname !== "/login") {
      localStorage.clear();
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const s = localStorage.getItem("erp_user");
    return s ? JSON.parse(s) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem("erp_token"));

  const isAdmin = user?.role === "admin";
  // iter303 — GMP portalında UPDATE/DELETE için Admin OR Sistem Yetkilisi.
  const isSystemAuthority = !!user?.system_authority;
  const canGmpEditDelete = isAdmin || isSystemAuthority;

  useEffect(() => {
    if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    else delete axios.defaults.headers.common["Authorization"];
    if (typeof document !== "undefined") {
      const roleClass = user?.role ? `role-${user.role}` : "role-anon";
      document.body.className = document.body.className.replace(/\brole-\S+/g, "").trim() + " " + roleClass;
    }
  }, [token, user]);

  const login = async (usernameOrEmail, password) => {
    // Spec msg-USR rules 22-23: primary login is username. Backend also accepts
    // email transparently for back-compat.
    const isEmail = typeof usernameOrEmail === "string" && usernameOrEmail.includes("@");
    const body = isEmail
      ? { email: usernameOrEmail, password }
      : { username: usernameOrEmail, password };
    const { data } = await axios.post(`${API}/auth/login`, body);
    // fetch permissions
    let perms = {};
    let actions = {};
    try {
      const p = await axios.get(`${API}/me/permissions`, { headers: { Authorization: `Bearer ${data.token}` } });
      perms = p.data.modules || {};
      actions = p.data.actions || {};
      // iter304 — system_authority flag'i backend authoritative kaynak;
      // login response ile permissions endpoint her ikisi de içeriyor.
      if (typeof p.data.system_authority === "boolean") {
        data.user.system_authority = p.data.system_authority;
      }
    } catch (e) { console.error("permissions fetch failed:", e); }
    const userWithPerms = { ...data.user, module_permissions: perms, action_permissions: actions };
    localStorage.setItem("erp_token", data.token);
    localStorage.setItem("erp_user", JSON.stringify(userWithPerms));
    setToken(data.token);
    setUser(userWithPerms);
    return userWithPerms;
  };
  const logout = () => {
    try {
      // Close SSE stream so the stale JWT is not reused after sign-out.
      // Lazy import to avoid a circular dep between auth.jsx and events.js.
      const evMod = require("./events");
      evMod.closeEventStream?.();
    } catch (e) { /* ignore */ }
    localStorage.clear();
    setToken(null);
    setUser(null);
  };
  return <AuthCtx.Provider value={{ user, token, login, logout, API, isAdmin, isSystemAuthority, canGmpEditDelete }}>{children}</AuthCtx.Provider>;
}
export const useAuth = () => useContext(AuthCtx);
export { API };
